import React, { useState, useEffect, useRef } from 'react'
import { api, formatCurrency } from '../../utils/api'
import { PRODUCT_TYPES } from '../../utils/constants'
import PinButton from './PinButton'

function ProductDetailModal({ product, collections = [], onClose, onSave, onDelete, onRelist, canEditPrice = false, canEditQty = false }) {
  // Get price from product or first variant
  const productPrice = product.price || product.variants?.[0]?.price || null
  const productCompareAtPrice = product.compare_at_price || product.variants?.[0]?.compare_at_price || ''
  const productCost = product.cost || product.variants?.[0]?.cost || '0'
  const productQuantity = product.variants?.[0]?.inventory_quantity ?? product.quantity ?? 0

  const [formData, setFormData] = useState({
    title: product.title || '',
    description: product.description || product.body_html || '',
    product_type: product.product_type || '',
    vendor: product.vendor || '',
    cost: productCost,
    compare_at_price: productCompareAtPrice,
    tags: Array.isArray(product.tags) ? product.tags.join(', ') : (product.tags || ''),
    collections: product.collections || [],
    quantity: productQuantity,
    ...(canEditPrice ? { price: productPrice || '' } : {})
  })
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [showHtmlView, setShowHtmlView] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  // Image editing state
  const [imageAdjustments, setImageAdjustments] = useState({
    brightness: 100,
    contrast: 100,
    autoApplied: false
  })
  const [cropMode, setCropMode] = useState(false)
  const [cropArea, setCropArea] = useState({ x: 0, y: 0, width: 100, height: 100 })
  const [savingImage, setSavingImage] = useState(false)
  const [removingBg, setRemovingBg] = useState(false)
  const [makingRatio, setMakingRatio] = useState(false)
  const [autoProcessing, setAutoProcessing] = useState(false)
  const [autoProcessStep, setAutoProcessStep] = useState('')
  const [processedImage, setProcessedImage] = useState(null) // Stores base64 of BG-removed or ratio-adjusted image
  const [bgModel, setBgModel] = useState('isnet-general-use')
  const [bgSource, setBgSource] = useState(null) // Which service processed the image
  const [bgCredits, setBgCredits] = useState(null) // remove.bg credits remaining
  const [orderedImages, setOrderedImages] = useState(product.images || [])
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [savingOrder, setSavingOrder] = useState(false)
  const [orderChanged, setOrderChanged] = useState(false)
  const imageRef = useRef(null)
  const cropStartRef = useRef(null)

  const images = orderedImages
  const hasImages = images.length > 0

  // Sync orderedImages when product changes
  useEffect(() => {
    setOrderedImages(product.images || [])
    setOrderChanged(false)
  }, [product.id])

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    if (!lightboxOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false)
      if (e.key === 'ArrowLeft') prevImage()
      if (e.key === 'ArrowRight') nextImage()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen, images.length])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setHasChanges(true)
    setSaved(false)
  }

  const handleCollectionToggle = (collectionId) => {
    setFormData(prev => {
      const current = prev.collections || []
      if (current.includes(collectionId)) {
        return { ...prev, collections: current.filter(id => id !== collectionId) }
      } else {
        return { ...prev, collections: [...current, collectionId] }
      }
    })
    setHasChanges(true)
    setSaved(false)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave(product.id, formData)
      setSaved(true)
      setHasChanges(false)
    } catch (e) {
      alert('Failed to save: ' + e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm('This will permanently delete this item from both Shopify and Prism. This action cannot be undone. Are you sure?')) return
    setDeleting(true)
    try {
      await onDelete(product.id)
    } catch (e) {
      alert('Failed to delete: ' + e.message)
      setDeleting(false)
    }
  }

  const prevImage = () => {
    setCurrentImageIndex(i => (i > 0 ? i - 1 : images.length - 1))
    resetImageAdjustments()
  }

  const nextImage = () => {
    setCurrentImageIndex(i => (i < images.length - 1 ? i + 1 : 0))
    resetImageAdjustments()
  }

  const resetImageAdjustments = () => {
    setImageAdjustments({ brightness: 100, contrast: 100, autoApplied: false })
    setCropMode(false)
    setProcessedImage(null)
    setBgSource(null)
    setBgCredits(null)
  }

  const handleAutoLevel = () => {
    // Apply automatic brightness/contrast adjustment
    setImageAdjustments({
      brightness: 105,
      contrast: 110,
      autoApplied: true
    })
  }

  const handleResetImage = () => {
    resetImageAdjustments()
  }

  const handleBrightnessChange = (value) => {
    setImageAdjustments(prev => ({ ...prev, brightness: value, autoApplied: false }))
  }

  const handleContrastChange = (value) => {
    setImageAdjustments(prev => ({ ...prev, contrast: value, autoApplied: false }))
  }

  const getImageFilter = () => {
    return `brightness(${imageAdjustments.brightness}%) contrast(${imageAdjustments.contrast}%)`
  }

  const getEditedImageDataUrl = () => {
    return new Promise((resolve, reject) => {
      const imgSrc = processedImage || images[currentImageIndex]?.src
      if (!imgSrc) {
        reject(new Error('No image selected'))
        return
      }

      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const image = new Image()
      image.crossOrigin = 'anonymous'

      image.onload = () => {
        if (cropMode && cropArea.width < 100) {
          // Apply crop
          const scaleX = image.width / 100
          const scaleY = image.height / 100
          const sx = cropArea.x * scaleX
          const sy = cropArea.y * scaleY
          const sw = cropArea.width * scaleX
          const sh = cropArea.height * scaleY

          canvas.width = sw
          canvas.height = sh
          ctx.filter = getImageFilter()
          ctx.drawImage(image, sx, sy, sw, sh, 0, 0, sw, sh)
        } else {
          canvas.width = image.width
          canvas.height = image.height
          ctx.filter = getImageFilter()
          ctx.drawImage(image, 0, 0)
        }

        // Use PNG format if background was removed (to preserve transparency)
        if (processedImage) {
          resolve(canvas.toDataURL('image/png'))
        } else {
          resolve(canvas.toDataURL('image/jpeg', 0.9))
        }
      }

      image.onerror = () => reject(new Error('Failed to load image'))
      image.src = imgSrc
    })
  }

  const handleDownloadImage = async () => {
    try {
      const dataUrl = await getEditedImageDataUrl()
      const link = document.createElement('a')
      link.download = `${product.title || 'image'}-edited.jpg`
      link.href = dataUrl
      link.click()
    } catch (err) {
      console.error('Failed to download image:', err)
      alert('Failed to download image. The image may have CORS restrictions.')
    }
  }

  const handleSaveToShopify = async () => {
    const img = images[currentImageIndex]
    if (!img?.id) {
      alert('Cannot save: Image ID not found')
      return
    }

    setSavingImage(true)
    try {
      const dataUrl = await getEditedImageDataUrl()
      // Remove the data:image/xxx;base64, prefix
      const base64Data = dataUrl.split(',')[1]

      // Use PNG extension if background was removed
      const ext = processedImage ? 'png' : 'jpg'

      await api.post(`/api/inventory/shopify/product/${product.id}/image`, {
        image_id: img.id,
        image_data: base64Data,
        filename: `${product.title || 'image'}-edited.${ext}`,
        position: currentImageIndex + 1  // Preserve original position (1-based)
      })

      alert('Image saved to Shopify successfully!')
      setProcessedImage(null) // Clear processed state after save
    } catch (err) {
      console.error('Failed to save to Shopify:', err)
      alert('Failed to save to Shopify: ' + err.message)
    } finally {
      setSavingImage(false)
    }
  }

  const handleRemoveBackground = async () => {
    const img = images[currentImageIndex]
    if (!img?.src) {
      alert('No image selected')
      return
    }

    setRemovingBg(true)
    try {
      const response = await api.post('/api/image/remove-background', {
        image_url: img.src,
        model: bgModel,
        alpha_matting: true,
        alpha_matting_foreground_threshold: 270,
        alpha_matting_background_threshold: 20,
        alpha_matting_erode_size: 10,
        post_process_mask: true
      })

      if (response.success && response.image_data) {
        setProcessedImage(`data:${response.content_type};base64,${response.image_data}`)
        setBgSource(response.source || null)
        if (response.credits_remaining !== undefined) setBgCredits(response.credits_remaining)
      } else {
        throw new Error('No image data returned')
      }
    } catch (err) {
      console.error('Background removal failed:', err)
      if (err.message?.includes('402')) {
        alert('Monthly quota exceeded. Try again next month.')
      } else if (err.message?.includes('429')) {
        alert('Rate limit exceeded. Please wait and try again.')
      } else {
        alert('Background removal failed: ' + (err.message || 'Unknown error'))
      }
    } finally {
      setRemovingBg(false)
    }
  }

  const handleUseProcessedImage = () => {
    // This will be used when saving to Shopify - use the processed image instead
    setProcessedImage(null) // Reset after use
  }

  const handleDiscardProcessedImage = () => {
    setProcessedImage(null)
    setBgSource(null)
  }

  const handleMake4x5Ratio = async () => {
    const imgSrc = processedImage || images[currentImageIndex]?.src
    if (!imgSrc) {
      alert('No image selected')
      return
    }

    setMakingRatio(true)
    try {
      await new Promise((resolve, reject) => {
        const image = new Image()
        image.crossOrigin = 'anonymous'

        image.onload = () => {
          const origWidth = image.width
          const origHeight = image.height

          // Target ratio is 4:5 (width:height) - portrait format
          const targetRatio = 4 / 5  // 0.8 - taller than wide
          const currentRatio = origWidth / origHeight

          let newWidth, newHeight

          if (currentRatio > targetRatio) {
            // Image is wider than 4:5 - need to add whitespace to top/bottom
            newWidth = origWidth
            newHeight = Math.round(origWidth / targetRatio)
          } else if (currentRatio < targetRatio) {
            // Image is taller than 4:5 - need to add whitespace to left/right
            newHeight = origHeight
            newWidth = Math.round(origHeight * targetRatio)
          } else {
            // Already 4:5 ratio
            alert('Image is already 4:5 ratio')
            resolve()
            return
          }

          // Create canvas with white background
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          canvas.width = newWidth
          canvas.height = newHeight

          // Fill with white background
          ctx.fillStyle = '#FFFFFF'
          ctx.fillRect(0, 0, newWidth, newHeight)

          // Center the original image on the canvas
          const offsetX = Math.round((newWidth - origWidth) / 2)
          const offsetY = Math.round((newHeight - origHeight) / 2)

          // Apply any current brightness/contrast adjustments
          ctx.filter = getImageFilter()
          ctx.drawImage(image, offsetX, offsetY)

          // Export as PNG (to preserve any transparency from BG removal)
          const dataUrl = canvas.toDataURL('image/png')
          setProcessedImage(dataUrl)
          resolve()
        }

        image.onerror = () => reject(new Error('Failed to load image'))
        image.src = imgSrc
      })
    } catch (err) {
      console.error('4:5 ratio conversion failed:', err)
      alert('Failed to convert image: ' + err.message)
    } finally {
      setMakingRatio(false)
    }
  }

  const handleAutoProcessAll = async () => {
    const img = images[currentImageIndex]
    if (!img?.src) {
      alert('No image selected')
      return
    }

    setAutoProcessing(true)
    try {
      // Step 1: Auto colour correction
      setAutoProcessStep('Applying colour correction...')
      const adjustments = { brightness: 105, contrast: 110, autoApplied: true }
      setImageAdjustments(adjustments)
      const filterStr = `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%)`

      // Step 2: Remove background
      setAutoProcessStep('Removing background...')
      const response = await api.post('/api/image/remove-background', {
        image_url: img.src,
        model: bgModel,
        alpha_matting: true,
        alpha_matting_foreground_threshold: 270,
        alpha_matting_background_threshold: 20,
        alpha_matting_erode_size: 10,
        post_process_mask: true
      })
      if (!response.success || !response.image_data) {
        throw new Error('Background removal failed: No image data returned')
      }
      const bgRemovedImage = `data:${response.content_type};base64,${response.image_data}`
      setBgSource(response.source || null)
      if (response.credits_remaining !== undefined) setBgCredits(response.credits_remaining)

      // Step 3: 4:5 portrait ratio
      setAutoProcessStep('Applying 4:5 portrait ratio...')
      const finalImage = await new Promise((resolve, reject) => {
        const image = new Image()
        image.crossOrigin = 'anonymous'
        image.onload = () => {
          const origWidth = image.width
          const origHeight = image.height
          const targetRatio = 4 / 5
          const currentRatio = origWidth / origHeight

          let newWidth, newHeight
          if (currentRatio > targetRatio) {
            newWidth = origWidth
            newHeight = Math.round(origWidth / targetRatio)
          } else if (currentRatio < targetRatio) {
            newHeight = origHeight
            newWidth = Math.round(origHeight * targetRatio)
          } else {
            // Already 4:5 — just apply the filter to the BG-removed image
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')
            canvas.width = origWidth
            canvas.height = origHeight
            ctx.filter = filterStr
            ctx.drawImage(image, 0, 0)
            resolve(canvas.toDataURL('image/png'))
            return
          }

          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          canvas.width = newWidth
          canvas.height = newHeight
          ctx.fillStyle = '#FFFFFF'
          ctx.fillRect(0, 0, newWidth, newHeight)
          const offsetX = Math.round((newWidth - origWidth) / 2)
          const offsetY = Math.round((newHeight - origHeight) / 2)
          ctx.filter = filterStr
          ctx.drawImage(image, offsetX, offsetY)
          resolve(canvas.toDataURL('image/png'))
        }
        image.onerror = () => reject(new Error('Failed to load image for 4:5 conversion'))
        image.src = bgRemovedImage
      })

      setProcessedImage(finalImage)
      setAutoProcessStep('')
    } catch (err) {
      console.error('Auto process failed:', err)
      // Roll back all changes on failure
      resetImageAdjustments()
      if (err.message?.includes('402')) {
        alert('Auto Process failed: Monthly BG removal quota exceeded (50 free/month)')
      } else if (err.message?.includes('429')) {
        alert('Auto Process failed: Rate limit exceeded. Please wait and try again.')
      } else {
        alert('Auto Process failed: ' + (err.message || 'Unknown error'))
      }
    } finally {
      setAutoProcessing(false)
      setAutoProcessStep('')
    }
  }

  const handleToggleCrop = async () => {
    if (cropMode) {
      // Apply the crop
      if (cropArea.width < 100 || cropArea.height < 100) {
        try {
          const imgSrc = processedImage || images[currentImageIndex]?.src
          if (!imgSrc) {
            setCropMode(false)
            setCropArea({ x: 0, y: 0, width: 100, height: 100 })
            return
          }

          await new Promise((resolve, reject) => {
            const image = new Image()
            image.crossOrigin = 'anonymous'

            image.onload = () => {
              const canvas = document.createElement('canvas')
              const ctx = canvas.getContext('2d')

              // Calculate crop dimensions based on percentage values
              const scaleX = image.width / 100
              const scaleY = image.height / 100
              const sx = cropArea.x * scaleX
              const sy = cropArea.y * scaleY
              const sw = cropArea.width * scaleX
              const sh = cropArea.height * scaleY

              canvas.width = sw
              canvas.height = sh
              ctx.filter = getImageFilter()
              ctx.drawImage(image, sx, sy, sw, sh, 0, 0, sw, sh)

              const dataUrl = canvas.toDataURL('image/png')
              setProcessedImage(dataUrl)
              resolve()
            }

            image.onerror = () => reject(new Error('Failed to load image'))
            image.src = imgSrc
          })
        } catch (err) {
          console.error('Crop failed:', err)
          alert('Failed to apply crop: ' + err.message)
        }
      }
      // Exit crop mode
      setCropMode(false)
      setCropArea({ x: 0, y: 0, width: 100, height: 100 })
    } else {
      // Enter crop mode with default selection
      setCropMode(true)
      setCropArea({ x: 10, y: 10, width: 80, height: 80 })
    }
  }

  const handleCropMouseDown = (e) => {
    if (!cropMode) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100
    cropStartRef.current = { x, y }
  }

  const handleCropMouseMove = (e) => {
    if (!cropMode || !cropStartRef.current) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100

    const startX = Math.min(cropStartRef.current.x, x)
    const startY = Math.min(cropStartRef.current.y, y)
    const width = Math.abs(x - cropStartRef.current.x)
    const height = Math.abs(y - cropStartRef.current.y)

    setCropArea({
      x: Math.max(0, startX),
      y: Math.max(0, startY),
      width: Math.min(100 - startX, width),
      height: Math.min(100 - startY, height)
    })
  }

  const handleCropMouseUp = () => {
    cropStartRef.current = null
  }

  // Image reordering drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', e.target.outerHTML)
    e.target.style.opacity = '0.5'
  }

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1'
    setDraggedIndex(null)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e, dropIndex) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === dropIndex) return

    const newImages = [...orderedImages]
    const draggedImage = newImages[draggedIndex]
    newImages.splice(draggedIndex, 1)
    newImages.splice(dropIndex, 0, draggedImage)

    setOrderedImages(newImages)
    setOrderChanged(true)
    setCurrentImageIndex(dropIndex)
  }

  const handleSaveImageOrder = async () => {
    if (!orderChanged || orderedImages.length === 0) return

    setSavingOrder(true)
    try {
      // Build array of image IDs in new order
      const imageOrder = orderedImages.map((img, idx) => ({
        id: img.id,
        position: idx + 1
      }))

      await api.post(`/api/inventory/shopify/product/${product.id}/images/reorder`, {
        images: imageOrder
      })

      setOrderChanged(false)
      alert('Image order saved!')
    } catch (err) {
      console.error('Failed to save image order:', err)
      alert('Failed to save image order: ' + err.message)
    } finally {
      setSavingOrder(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-product-detail" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Edit Product</h2>
            <span
              style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', fontFamily: 'monospace', cursor: 'pointer' }}
              onClick={() => { navigator.clipboard.writeText(product.id); }}
              title="Click to copy product ID"
            >
              ID: {product.id}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <PinButton
              entityType="product"
              entityId={product.id}
              entityName={product.title}
            />
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        <div className="modal-body-scroll">
          <div className="product-detail-layout">
            {/* Image Gallery */}
            <div className="product-image-section">
              {hasImages ? (
                <div className="product-image-gallery">
                  <img
                    src={images[currentImageIndex]?.src}
                    alt={product.title}
                    className="product-main-image"
                    onClick={() => setLightboxOpen(true)}
                    style={{ cursor: 'pointer' }}
                    title="Click to enlarge"
                  />
                  {images.length > 1 && (
                    <div className="image-navigation">
                      <button className="btn btn-ghost btn-sm" onClick={prevImage}>&lt;</button>
                      <span>{currentImageIndex + 1} / {images.length}</span>
                      <button className="btn btn-ghost btn-sm" onClick={nextImage}>&gt;</button>
                    </div>
                  )}
                  {/* Thumbnail strip - draggable for reordering */}
                  {images.length > 1 && (
                    <div className="image-thumbnails-container">
                      <div className="image-thumbnails">
                        {images.map((img, idx) => (
                          <div
                            key={img.id || idx}
                            className={`image-thumbnail-wrapper ${idx === currentImageIndex ? 'active' : ''} ${draggedIndex === idx ? 'dragging' : ''}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, idx)}
                            onDragEnd={handleDragEnd}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, idx)}
                          >
                            <img
                              src={img.src}
                              alt=""
                              className="image-thumbnail"
                              onClick={() => setCurrentImageIndex(idx)}
                            />
                            <span className="thumbnail-position">{idx + 1}</span>
                          </div>
                        ))}
                      </div>
                      {orderChanged && (
                        <button
                          className="btn btn-sm btn-primary save-order-btn"
                          onClick={handleSaveImageOrder}
                          disabled={savingOrder}
                        >
                          {savingOrder ? 'Saving...' : 'Save Order'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="product-no-image">No Image</div>
              )}
            </div>

            {/* Form Fields */}
            <div className="product-form-section">
              <div className="form-group">
                <label className="form-label">Title</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.title}
                  onChange={e => handleInputChange('title', e.target.value)}
                />
              </div>

              <div className="form-row-4">
                <div className="form-group">
                  <label className="form-label">{canEditPrice ? 'Price' : 'Price (read-only)'}</label>
                  {canEditPrice ? (
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="form-input"
                      value={formData.price}
                      onChange={e => handleInputChange('price', e.target.value)}
                    />
                  ) : (
                    <input
                      type="text"
                      className="form-input"
                      value={formatCurrency(productPrice)}
                      disabled
                      title="Use Repricing tab to change price"
                    />
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Cost</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-input"
                    value={formData.cost}
                    onChange={e => handleInputChange('cost', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Compare At Price</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-input"
                    value={formData.compare_at_price}
                    onChange={e => handleInputChange('compare_at_price', e.target.value)}
                    placeholder="Optional"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{canEditQty ? 'Qty' : 'Qty (read-only)'}</label>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    className="form-input"
                    value={formData.quantity}
                    onChange={e => handleInputChange('quantity', e.target.value)}
                    disabled={!canEditQty}
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="description-header">
                  <label className="form-label">Description</label>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setShowHtmlView(!showHtmlView)}
                  >
                    {showHtmlView ? 'Edit Visually' : 'Edit HTML'}
                  </button>
                </div>
                {showHtmlView ? (
                  <textarea
                    className="form-input form-textarea"
                    value={formData.description}
                    onChange={e => handleInputChange('description', e.target.value)}
                    rows={4}
                  />
                ) : (
                  <div
                    className="description-preview description-editable"
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={e => handleInputChange('description', e.target.innerHTML)}
                    dangerouslySetInnerHTML={{ __html: formData.description || '' }}
                  />
                )}
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label className="form-label">Product Type</label>
                  <select
                    className="form-input"
                    value={formData.product_type}
                    onChange={e => handleInputChange('product_type', e.target.value)}
                  >
                    <option value="">Select type...</option>
                    {PRODUCT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Vendor</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.vendor}
                    onChange={e => handleInputChange('vendor', e.target.value)}
                  />
                </div>
              </div>

              {collections.length > 0 && (
                <div className="form-group">
                  <label className="form-label">Collection(s)</label>
                  <div className="collection-checkboxes">
                    {collections.map(c => (
                      <label key={c.id} className="collection-checkbox-item">
                        <input
                          type="checkbox"
                          checked={formData.collections.includes(c.id)}
                          onChange={() => handleCollectionToggle(c.id)}
                        />
                        <span>{c.title}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Tags</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.tags}
                  onChange={e => handleInputChange('tags', e.target.value)}
                  placeholder="tag1, tag2, tag3"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            {onDelete && (
              <button
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting || saving}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
            {onRelist && (
              <button
                className="btn btn-primary"
                onClick={() => onRelist(product)}
                disabled={saving || deleting}
              >
                Relist
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={saving || deleting}>
              Close
            </button>
            <button
              className={`btn ${saved && !hasChanges ? 'btn-success' : hasChanges ? 'btn-primary' : 'btn-secondary'}`}
              onClick={handleSave}
              disabled={saving || !hasChanges || deleting}
            >
              {saving ? 'Saving...' : saved && !hasChanges ? 'Saved' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Image Lightbox */}
      {lightboxOpen && hasImages && (
        <div className="lightbox-overlay" onClick={() => { setLightboxOpen(false); resetImageAdjustments(); }}>
          <div className="lightbox-content lightbox-with-controls" onClick={e => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => { setLightboxOpen(false); resetImageAdjustments(); }}>&times;</button>

            {images.length > 1 && !cropMode && (
              <button className="lightbox-nav lightbox-prev" onClick={prevImage}>&lt;</button>
            )}

            <div
              className="lightbox-image-container"
              onMouseDown={handleCropMouseDown}
              onMouseMove={handleCropMouseMove}
              onMouseUp={handleCropMouseUp}
              onMouseLeave={handleCropMouseUp}
            >
              <img
                ref={imageRef}
                src={processedImage || images[currentImageIndex]?.src}
                alt={product.title}
                className="lightbox-image"
                style={{ filter: getImageFilter(), background: processedImage ? '#f0f0f0' : 'transparent' }}
                draggable={false}
              />
              {cropMode && (
                <div className="crop-overlay">
                  <div
                    className="crop-selection"
                    style={{
                      left: `${cropArea.x}%`,
                      top: `${cropArea.y}%`,
                      width: `${cropArea.width}%`,
                      height: `${cropArea.height}%`
                    }}
                  />
                </div>
              )}
            </div>

            {images.length > 1 && !cropMode && (
              <button className="lightbox-nav lightbox-next" onClick={nextImage}>&gt;</button>
            )}

            {/* Image Editing Controls */}
            <div className="lightbox-controls">
              <div className="lightbox-controls-row">
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleAutoProcessAll}
                  disabled={autoProcessing || removingBg || makingRatio}
                  title="Auto Level + Remove BG + 4:5 Portrait in one click"
                >
                  {autoProcessing ? autoProcessStep || 'Processing...' : 'Auto Process All'}
                </button>
                <button className="btn btn-sm btn-secondary" onClick={handleAutoLevel} disabled={autoProcessing}>
                  Auto Level
                </button>
                <button
                  className={`btn btn-sm ${cropMode ? 'btn-accent' : 'btn-secondary'}`}
                  onClick={handleToggleCrop}
                  disabled={autoProcessing}
                >
                  {cropMode ? 'Apply Crop' : 'Crop'}
                </button>
                <button
                  className={`btn btn-sm ${processedImage ? 'btn-accent' : 'btn-secondary'}`}
                  onClick={processedImage ? handleDiscardProcessedImage : handleRemoveBackground}
                  disabled={removingBg || makingRatio || autoProcessing}
                >
                  {removingBg ? 'Processing...' : processedImage ? 'Undo BG' : 'Remove BG'}
                </button>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={handleMake4x5Ratio}
                  disabled={makingRatio || removingBg || autoProcessing}
                  title="Add whitespace to make 4:5 portrait ratio"
                >
                  {makingRatio ? 'Processing...' : '4:5 Portrait'}
                </button>
                <button className="btn btn-sm btn-secondary" onClick={handleResetImage} disabled={autoProcessing}>
                  Reset
                </button>
              </div>
              <div className="lightbox-controls-row">
                <label className="lightbox-slider-label">
                  Brightness: {imageAdjustments.brightness}%
                  <input
                    type="range"
                    min="50"
                    max="150"
                    value={imageAdjustments.brightness}
                    onChange={e => handleBrightnessChange(parseInt(e.target.value))}
                    className="lightbox-slider"
                  />
                </label>
                <label className="lightbox-slider-label">
                  Contrast: {imageAdjustments.contrast}%
                  <input
                    type="range"
                    min="50"
                    max="150"
                    value={imageAdjustments.contrast}
                    onChange={e => handleContrastChange(parseInt(e.target.value))}
                    className="lightbox-slider"
                  />
                </label>
                <label className="lightbox-slider-label">
                  BG Model:
                  <select
                    value={bgModel}
                    onChange={e => setBgModel(e.target.value)}
                    style={{ marginLeft: 4, padding: '2px 4px', fontSize: '0.8rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)', background: '#1a1a2e', color: '#fff' }}
                  >
                    <option value="isnet-general-use">ISNet (best)</option>
                    <option value="u2net">U2Net</option>
                    <option value="u2netp">U2Net Lite (fast)</option>
                    <option value="silueta">Silueta (small)</option>
                  </select>
                </label>
              </div>
              <div className="lightbox-controls-row">
                <button className="btn btn-sm btn-secondary" onClick={handleDownloadImage}>
                  Download
                </button>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleSaveToShopify}
                  disabled={savingImage}
                >
                  {savingImage ? 'Saving...' : 'Save to Shopify'}
                </button>
              </div>
              {cropMode && (
                <div className="lightbox-crop-hint">
                  Drag on image to select crop area
                </div>
              )}
              {processedImage && (
                <div className="lightbox-crop-hint" style={{ color: '#4ade80' }}>
                  Image modified{bgSource ? ` (via ${bgSource})` : ''}{bgCredits !== null ? ` — ${bgCredits} credits left` : ''} - Click "Save to Shopify" to keep changes
                </div>
              )}
            </div>

            {images.length > 1 && (
              <div className="lightbox-counter">
                {currentImageIndex + 1} / {images.length}
              </div>
            )}

            {images.length > 1 && (
              <div className="lightbox-thumbnails">
                {images.map((img, idx) => (
                  <img
                    key={idx}
                    src={img.src}
                    alt=""
                    className={`lightbox-thumb ${idx === currentImageIndex ? 'active' : ''}`}
                    onClick={() => { setCurrentImageIndex(idx); resetImageAdjustments(); }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ProductDetailModal
